// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { TerceraChartCashItemSeriesCacheScreenDataDateChangeType, TerceraChartCashItemSeriesDataType } from '../Series/TerceraChartCashItemSeriesEnums';
import { DataCacheToolType } from '@shared/commons/cache/DataCacheToolEnums';
import { ModelDataType, ChartLoadTrigger, TerceraChartMVCCommand } from '../TerceraChartMVC';
import { TerceraChartUtils } from '../TerceraChartUtils';
import { PanelNames } from '@front/controls/UtilsClasses/FactoryConstants';
import { ChartDataType, TerceraChartTradingOperation } from '../Utils/ChartConstants';
import { TerceraChartDayHighLowRenderer } from '../Renderers/TerceraChartDayHighLowRenderer';
import { TerceraChartTimeToNextBarRenderer } from '../Renderers/TerceraChartTimeToNextBarRenderer';
import { TerceraChartNewToolRenderer } from '../Renderers/Tools/TerceraChartNewToolRenderer';
import { TerceraChartToolRenderer } from '../Renderers/Tools/TerceraChartToolRenderer';
import { TradeAnalysisType } from '@shared/utils/History/TFInfo';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DynProperty } from '@shared/commons/DynProperty';
import { IndicatorManager } from '@shared/commons/cache/indicators/IndicatorManager';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { TerceraChartActionEnum, TerceraChartAction, TerceraChartToolbarsEnum, CancelActionContext } from '../TerceraChartAction';
import { TerceraChartActionState } from '../TerceraChartActionState';
import { DataCache } from '@shared/commons/DataCache';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { ITerceraChartShowScreenType } from '../ITerceraChartShowScreenType';
import { TerceraChartInfoWindowRenderer } from '../Renderers/TerceraChartInfoWindowRenderer';
import { addOverlayScreenHandler, propertySetupScreenForIndicatorHandler } from '@shared/utils/AppHandlers';
import { Position } from '@shared/commons/cache/Position';
import { TerceraChartBaseActionProcessor } from './TerceraChartBaseActionProcessor';
import { InfoWindowDockingType } from '../Renderers/InfoWindow/InfoWindowDockingType';
import { InfoWindowDisplayMode } from '../Renderers/InfoWindow/InfoWindowDisplayMode';
import { ChartSeriesDataConverter } from '../Series/ChartSeriesDataConverter';
export class TerceraChartActionProcessor extends TerceraChartBaseActionProcessor {
    initializeActions() {
        super.initializeActions();
        this.actionsProcessor[TerceraChartActionEnum.SymbolsLookup] = this.processSymbolsLookupAction;
        this.actionsProcessor[TerceraChartActionEnum.Eraser] = this.processEraserAction;
        this.actionsProcessor[TerceraChartActionEnum.ToolSelector] = this.processToolSelectorAction;
        this.actionsProcessor[TerceraChartActionEnum.CreationTool] = this.processCreationToolAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateIndicatorSettings] = this.plateIndicatorSettingsAction;
        this.actionsProcessor[TerceraChartActionEnum.View] = this.processViewAction;
        this.actionsProcessor[TerceraChartActionEnum.SetStayInDrawingMode] = this.setStayInDrawingModeAction;
        this.actionsProcessor[TerceraChartActionEnum.InfoWindowHide] = this.processInfoWindowHideAction;
        this.actionsProcessor[TerceraChartActionEnum.InfoWindowMode] = this.processInfoWindowModeAction;
        this.actionsProcessor[TerceraChartActionEnum.InfoWindowDock] = this.processInfoWindowDockAction;
        this.actionsProcessor[TerceraChartActionEnum.InfoWindowShortMode] = this.processInfoWindowShortModeAction;
        this.actionsProcessor[TerceraChartActionEnum.InfoWindowSettings] = this.processInfoWindowSettingsAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateOverlaySettings] = this.plateOverlaySettingsAction;
        this.actionsProcessor[TerceraChartActionEnum.HoverAndSelectedToolSettings] =
            this.processHoverAndSelectedToolSettingsAction;
        this.actionsProcessor[TerceraChartActionEnum.CloneTool] = this.processCloneToolAction;
        this.actionsProcessor[TerceraChartActionEnum.DeleteHoverAndSelectedTools] =
            this.processDeleteHoverAndSelectedToolsAction;
        this.actionsProcessor[TerceraChartActionEnum.Style] = this.processStyleAction;
        this.actionsProcessor[TerceraChartActionEnum.DataType] = this.processDataTypeAction;
        this.actionsProcessor[TerceraChartActionEnum.ClosePosition] = this.processClosePositionAction;
        this.actionsProcessor[TerceraChartActionEnum.CloseAllPosition] = this.processCloseAllPositionAction;
        this.actionsProcessor[TerceraChartActionEnum.ModifyPosition] = this.processModifyPositionAction;
        this.actionsProcessor[TerceraChartActionEnum.RollbackPosition] = this.processRollbackPositionAction;
        this.actionsProcessor[TerceraChartActionEnum.CancelOrders] = this.processCancelOrdersAction;
        this.actionsProcessor[TerceraChartActionEnum.CancelOrderID] = this.processCancelOrderIDAction;
        this.actionsProcessor[TerceraChartActionEnum.CancelOrdersAccount] = this.processCancelOrdersAccountAction;
        this.actionsProcessor[TerceraChartActionEnum.CancelOrdersSymbol] = this.processCancelOrdersSymbolAction;
        this.actionsProcessor[TerceraChartActionEnum.ModifyOrder] = this.processModifyOrderAction;
        this.actionsProcessor[TerceraChartActionEnum.ChangeToMarket] = this.processChangeToMarketAction;
        this.actionsProcessor[TerceraChartActionEnum.FitIndicators] = this.processFitIndicatorsAction;
        this.actionsProcessor[TerceraChartActionEnum.FitOrders] = this.processFitOrdersAction;
        this.actionsProcessor[TerceraChartActionEnum.FitDayHighLow] = this.processFitDayHighLowAction;
        this.actionsProcessor[TerceraChartActionEnum.FitDrawings] = this.processFitDrawingsAction;
        this.actionsProcessor[TerceraChartActionEnum.FitAlerts] = this.processFitAlertsAction;
        this.actionsProcessor[TerceraChartActionEnum.MainSymbol] = this.processMainSymbolAction;
        this.actionsProcessor[TerceraChartActionEnum.TimeSeparators] = this.processTimeSeparatorsAction;
        this.actionsProcessor[TerceraChartActionEnum.ShowHoles] = this.processShowHolesAction;
        this.actionsProcessor[TerceraChartActionEnum.ShowExtendedSession] = this.processShowExtendedSessionAction;
        this.actionsProcessor[TerceraChartActionEnum.Overlay] = this.processOverlayAction;
        this.actionsProcessor[TerceraChartActionEnum.Indicator] = this.processIndicatorAction;
        this.actionsProcessor[TerceraChartActionEnum.NewPanel] = this.processNewPanelAction;
        this.actionsProcessor[TerceraChartActionEnum.Refresh] = this.processRefreshAction;
        this.actionsProcessor[TerceraChartActionEnum.DeleteToolsByType] = this.processDeleteToolsByTypeAction;
        this.actionsProcessor[TerceraChartActionEnum.DeleteAllTools] = this.processDeleteAllToolsAction;
        this.actionsProcessor[TerceraChartActionEnum.ChartTrading] = this.processChartTradingAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateIndicatorVisible] = this.processPlateIndicatorVisibleAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateIndicatorDublicate] =
            this.processPlateIndicatorDublicateAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateIndicatorRemove] = this.processPlateIndicatorRemoveAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateOverlayVisible] = this.processPlateOverlayVisibleAction;
        this.actionsProcessor[TerceraChartActionEnum.PlateOverlayRemove] = this.processPlateOverlayRemoveAction;
        this.actionsProcessor[TerceraChartActionEnum.CancelOrderSLTP] = this.processCancelOrderSLTPAction;
        this.actionsProcessor[TerceraChartActionEnum.DrawingToolsGroup] = this.processDrawingToolsGroupAction;
        this.actionsProcessor[TerceraChartActionEnum.ToBegin] = this.processToBeginAction;
        this.actionsProcessor[TerceraChartActionEnum.ObjectManager] = this.processObjectManagerAction;
        this.actionsProcessor[TerceraChartActionEnum.ShowOnChart] = this.processShowOnChartAction;
        this.actionsProcessor[TerceraChartActionEnum.DeleteTool] = this.processDeleteToolAction;
        this.actionsProcessor[TerceraChartActionEnum.ToolSettings] = this.processToolSettingsAction;
        this.actionsProcessor[TerceraChartActionEnum.Absolute] = this.processChangeDataTypeAction;
        this.actionsProcessor[TerceraChartActionEnum.Relative] = this.processChangeDataTypeAction;
        this.actionsProcessor[TerceraChartActionEnum.Log] = this.processChangeDataTypeAction;
    }
    initializeStates() {
        super.initializeStates();
        this.statesProcessor[TerceraChartActionEnum.SymbolsLookup] = this.processSymbolsLookupState;
        this.statesProcessor[TerceraChartActionEnum.Eraser] = this.processEraserState;
        this.statesProcessor[TerceraChartActionEnum.ToolSelector] = this.processToolSelectorState;
        this.statesProcessor[TerceraChartActionEnum.CreationTool] = this.processCreationToolState;
        this.statesProcessor[TerceraChartActionEnum.PlateIndicatorSettings] = this.plateIndicatorSettingsState;
        this.statesProcessor[TerceraChartActionEnum.View] = this.processViewState;
        this.statesProcessor[TerceraChartActionEnum.PriceIndicator] = this.priceIndicatorState;
        this.statesProcessor[TerceraChartActionEnum.SetStayInDrawingMode] = this.setStayInDrawingModeState;
        this.statesProcessor[TerceraChartActionEnum.DeleteSelectedTools] = this.deleteSelectedToolsState;
        this.statesProcessor[TerceraChartActionEnum.SelectedToolSettings] = this.selectedToolSettingsState;
        this.statesProcessor[TerceraChartActionEnum.ToolColor] = this.toolColorState;
        this.statesProcessor[TerceraChartActionEnum.ToolLineWidth] = this.toolLineWidthState;
        this.statesProcessor[TerceraChartActionEnum.InfoWindowHide] = this.processInfoWindowHideState;
        this.statesProcessor[TerceraChartActionEnum.InfoWindowMode] = this.processInfoWindowModeState;
        this.statesProcessor[TerceraChartActionEnum.InfoWindowDock] = this.processInfoWindowDockState;
        this.statesProcessor[TerceraChartActionEnum.InfoWindowShortMode] = this.processInfoWindowShortModeState;
        this.statesProcessor[TerceraChartActionEnum.InfoWindowSettings] = this.processInfoWindowSettingsState;
        this.statesProcessor[TerceraChartActionEnum.PlateOverlaySettings] = this.plateOverlaySettingsState;
        this.statesProcessor[TerceraChartActionEnum.HoverAndSelectedToolSettings] =
            this.processHoverAndSelectedToolSettingsState;
        this.statesProcessor[TerceraChartActionEnum.CloneTool] = this.processCloneToolState;
        this.statesProcessor[TerceraChartActionEnum.DeleteHoverAndSelectedTools] =
            this.processDeleteHoverAndSelectedToolsState;
        this.statesProcessor[TerceraChartActionEnum.Style] = this.processStyleState;
        this.statesProcessor[TerceraChartActionEnum.DataType] = this.processDataTypeState;
        this.statesProcessor[TerceraChartActionEnum.ClosePosition] = this.processClosePositionState;
        this.statesProcessor[TerceraChartActionEnum.CloseAllPosition] = this.processCloseAllPositionState;
        this.statesProcessor[TerceraChartActionEnum.ModifyPosition] = this.processModifyPositionState;
        this.statesProcessor[TerceraChartActionEnum.RollbackPosition] = this.processRollbackPositionState;
        this.statesProcessor[TerceraChartActionEnum.CancelOrders] = this.processCancelOrdersState;
        this.statesProcessor[TerceraChartActionEnum.CancelOrderID] = this.processCancelOrderIDState;
        this.statesProcessor[TerceraChartActionEnum.CancelOrdersAccount] = this.processCancelOrdersAccountState;
        this.statesProcessor[TerceraChartActionEnum.CancelOrdersSymbol] = this.processCancelOrdersSymbolState;
        this.statesProcessor[TerceraChartActionEnum.ModifyOrder] = this.processModifyOrderState;
        this.statesProcessor[TerceraChartActionEnum.ChangeToMarket] = this.processChangeToMarketState;
        this.statesProcessor[TerceraChartActionEnum.FitIndicators] = this.processFitIndicatorsState;
        this.statesProcessor[TerceraChartActionEnum.FitOrders] = this.processFitOrdersState;
        this.statesProcessor[TerceraChartActionEnum.FitDayHighLow] = this.processFitDayHighLowState;
        this.statesProcessor[TerceraChartActionEnum.FitDrawings] = this.processFitDrawingsState;
        this.statesProcessor[TerceraChartActionEnum.FitAlerts] = this.processFitAlertsState;
        this.statesProcessor[TerceraChartActionEnum.MainSymbol] = this.processMainSymbolState;
        this.statesProcessor[TerceraChartActionEnum.TimeSeparators] = this.processTimeSeparatorsState;
        this.statesProcessor[TerceraChartActionEnum.ShowHoles] = this.processShowHolesState;
        this.statesProcessor[TerceraChartActionEnum.ShowExtendedSession] = this.processShowExtendedSessionState;
        this.statesProcessor[TerceraChartActionEnum.Overlay] = this.processOverlayState;
        this.statesProcessor[TerceraChartActionEnum.Indicator] = this.processIndicatorState;
        this.statesProcessor[TerceraChartActionEnum.NewPanel] = this.processNewPanelState;
        this.statesProcessor[TerceraChartActionEnum.Refresh] = this.processRefreshState;
        this.statesProcessor[TerceraChartActionEnum.DeleteToolsByType] = this.processDeleteToolsByTypeState;
        this.statesProcessor[TerceraChartActionEnum.DeleteAllTools] = this.processDeleteAllToolsState;
        this.statesProcessor[TerceraChartActionEnum.ChartTrading] = this.processChartTradingState;
        this.statesProcessor[TerceraChartActionEnum.PlateIndicatorVisible] = this.processPlateIndicatorVisibleState;
        this.statesProcessor[TerceraChartActionEnum.PlateIndicatorDublicate] = this.processPlateIndicatorDublicateState;
        this.statesProcessor[TerceraChartActionEnum.PlateIndicatorRemove] = this.processPlateIndicatorRemoveState;
        this.statesProcessor[TerceraChartActionEnum.PlateOverlayVisible] = this.processPlateOverlayVisibleState;
        this.statesProcessor[TerceraChartActionEnum.PlateOverlayRemove] = this.processPlateOverlayRemoveState;
        this.statesProcessor[TerceraChartActionEnum.CancelOrderSLTP] = this.processCancelOrderSLTPState;
        this.statesProcessor[TerceraChartActionEnum.ToBegin] = this.processToBeginState;
        this.statesProcessor[TerceraChartActionEnum.ObjectManager] = this.processObjectManagerState;
        this.statesProcessor[TerceraChartActionEnum.Absolute] = this.processChangeDataTypeState;
        this.statesProcessor[TerceraChartActionEnum.Relative] = this.processChangeDataTypeState;
        this.statesProcessor[TerceraChartActionEnum.Log] = this.processChangeDataTypeState;
    }
    processSymbolsLookupAction() {
        let processed = false;
        const chart = this.chart;
        if (!isNullOrUndefined(chart)) {
            const res = chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.InstrumentLookup, null);
            processed = !!res;
        }
        return processed;
    }
    processEraserAction(action, active) {
        var _a;
        let processed = false;
        if (((_a = this.chart) === null || _a === void 0 ? void 0 : _a.mainWindow) && this.chart.EraserRenderer) {
            const eraserRenderer = this.chart.EraserRenderer;
            const newValue = active !== null ? active : !eraserRenderer.Active;
            processed = eraserRenderer.Active !== newValue;
            eraserRenderer.Active = newValue;
        }
        return processed;
    }
    processToolSelectorAction(action, active) {
        var _a;
        let processed = false;
        if (((_a = this.chart) === null || _a === void 0 ? void 0 : _a.mainWindow) && this.chart.SelectionRenderer) {
            const selectionRenderer = this.chart.SelectionRenderer;
            const newValue = active !== null ? active : !selectionRenderer.Active;
            processed = selectionRenderer.Active !== newValue;
            selectionRenderer.Active = newValue;
        }
        return processed;
    }
    processCreationToolAction(action) {
        const processed = false;
        const toolType = action.Details;
        if (this.chart) {
            this.chart.NewToolStrategy.Reset(toolType);
        }
        return processed;
    }
    plateIndicatorSettingsAction(action) {
        const processed = false;
        const header = `${action.Details.Indicator.Script.ProjectName} ${Resources.getResource('screen.properties.common')}`;
        void propertySetupScreenForIndicatorHandler.editProperty(action.Details, action.Details.editPropertiesCallBackOnPlacedIndicator, header);
        return processed;
    }
    processViewAction(action) {
        const processed = false;
        const details = action.Details;
        const chart = this.chart;
        if (details === undefined || details === null || !chart) {
            return;
        }
        const wc = chart.windowsContainer;
        switch (details) {
            case TerceraChartToolbarsEnum.ScrollBar:
                wc.scrollerRenderer.Visible = !wc.scrollerRenderer.Visible;
                wc.LayoutWindows();
                break;
            case TerceraChartToolbarsEnum.OrderEntry:
            case TerceraChartToolbarsEnum.AnalyseToolBar:
            case TerceraChartToolbarsEnum.DrawingToolBar:
            case TerceraChartToolbarsEnum.MainToolBar:
                chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.UIManipulation, {
                    isSet: true,
                    toolBarType: details
                });
                break;
            case TerceraChartToolbarsEnum.PreviousCloseLine:
                if (typeof TerceraChartDayHighLowRenderer === 'function') {
                    const renderer = chart.mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
                    if (renderer) {
                        renderer.prevCloseVisible = !renderer.prevCloseVisible;
                    }
                }
                break;
            case TerceraChartToolbarsEnum.TimeToNextBar: {
                const renderer = chart.mainWindow.GetRenderer(TerceraChartTimeToNextBarRenderer);
                renderer.Visible = !renderer.Visible;
                break;
            }
            case TerceraChartToolbarsEnum.DayHighLow:
                if (typeof TerceraChartDayHighLowRenderer === 'function') {
                    const renderer = chart.mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
                    if (renderer) {
                        renderer.Visible = !renderer.Visible;
                    }
                }
                break;
            case TerceraChartToolbarsEnum.Volume: {
                const volumeBarsRenderer = chart.volumeBarsRenderer;
                volumeBarsRenderer.Visible = !volumeBarsRenderer.Visible;
                break;
            }
            case TerceraChartToolbarsEnum.GridTime: {
                const timeScaleRendererSettings = wc.xScaleRenderer.settings;
                timeScaleRendererSettings.ScaleGridVisibility = !timeScaleRendererSettings.ScaleGridVisibility;
                break;
            }
            case TerceraChartToolbarsEnum.GridPrice: {
                const yScaleRendererSettings = chart.yScaleRendererSettings;
                yScaleRendererSettings.ScaleGridVisibility = !yScaleRendererSettings.ScaleGridVisibility;
                break;
            }
        }
        chart.IsDirty();
        return processed;
    }
    setStayInDrawingModeAction() {
        if (this.chart != null) {
            const r = this.chart.mainWindow.GetMainThreadRenderer('TerceraChartNewToolRenderer');
            if (r != null) {
                r.StayInDrawingMode = !r.StayInDrawingMode;
            }
        }
    }
    processInfoWindowHideAction(action) {
        const processed = false;
        action.Details.displayMode = InfoWindowDisplayMode.Hidden;
        return processed;
    }
    processInfoWindowModeAction(action) {
        const processed = false;
        const infoWindowRenderer = this.chart.mainWindow.GetRenderer(TerceraChartInfoWindowRenderer);
        if (isNullOrUndefined(infoWindowRenderer)) {
            return processed;
        }
        if (!isNullOrUndefined(action.Details)) {
            infoWindowRenderer.displayMode = action.Details;
        }
        else {
            if (infoWindowRenderer.displayMode === InfoWindowDisplayMode.AttachedToCursor) {
                infoWindowRenderer.displayMode = InfoWindowDisplayMode.SeparateWindow;
            }
            else {
                infoWindowRenderer.displayMode = InfoWindowDisplayMode.AttachedToCursor;
            }
        }
        return processed;
    }
    processInfoWindowDockAction(action) {
        const processed = false;
        action.Details.DockingType =
            action.Details.DockingType === InfoWindowDockingType.None
                ? InfoWindowDockingType.Top
                : InfoWindowDockingType.None;
        return processed;
    }
    processInfoWindowShortModeAction(action) {
        const processed = false;
        action.Details.ShortMode = !action.Details.ShortMode;
        return processed;
    }
    processInfoWindowSettingsAction() {
        const processed = false;
        const parameters = { PanelSettingsDefaultPage: DynProperty.DATA_BOX_GROUP };
        this.chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.ChartSettings, parameters);
        return processed;
    }
    plateOverlaySettingsAction(action) {
        addOverlayScreenHandler.editOverlay(this.chart, action.Details);
    }
    processHoverAndSelectedToolSettingsAction(action, active) {
        const processed = false;
        let tool = action.Details;
        // If hovered(?) tool is null, then we try to get selected one.
        if (!tool)
            tool = this.chart.AllWindowsHoverAndSelectedTools[0];
        if (tool) {
            const parameters = { caller: tool };
            // parameters["Properties"] = tool.Properties();
            // parameters["caller"] = tool;
            // parameters["refreshHandler"] = new EmptyHandler(chart.RedrawBuffer);
            // parameters["allowResetToDefault"] = true;
            //
            this.chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.Properties, parameters);
        }
        return processed;
    }
    processCloneToolAction(action) {
        const processed = false;
        const tool = action.Details;
        if (tool) {
            const windows = this.chart.windowsContainer.Windows;
            const len = windows.length;
            for (let i = 0; i < len; i++) {
                const window = windows[i];
                const toolRenderer = window.GetRenderer(TerceraChartToolRenderer);
                if (toolRenderer.IsMyTool(tool)) {
                    toolRenderer.CloneTool(tool, window);
                }
            }
        }
        return processed;
    }
    processDeleteHoverAndSelectedToolsAction() {
        const processed = false;
        const windows = this.chart.windowsContainer.Windows;
        const len = windows.length;
        for (let i = 0; i < len; i++) {
            const toolRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
            toolRenderer.RemoveSelectedAndHoverTools();
        }
        return processed;
    }
    processStyleAction(action) {
        const processed = false;
        this.chart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.Style), action.Details);
        return processed;
    }
    processDataTypeAction(action) {
        this.chart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.HistoryType), action.Details.ChartDataType);
    }
    processClosePositionAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.ClosePosition
        });
        return processed;
    }
    processCloseAllPositionAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.CloseAllPosition
        });
        return processed;
    }
    processModifyPositionAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.ModifyOrderPosition
        });
        return processed;
    }
    processModifyOrderAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.ModifyOrderPosition
        });
        return processed;
    }
    processRollbackPositionAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.RollbackPosition
        });
        return processed;
    }
    processCancelOrdersAction(action) {
        const processed = false;
        return processed;
    }
    processCancelOrderIDAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.CancelOrder
        });
        return processed;
    }
    processCancelOrdersAccountAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.CancelOrdersAccount
        });
        return processed;
    }
    processCancelOrdersSymbolAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.CancelOrdersSymbol
        });
        return processed;
    }
    processChangeToMarketAction(action) {
        const processed = false;
        this.chart.terceraChartPanelContext.ChartVisualTrading(action.Details, {
            action: TerceraChartTradingOperation.ChangeToMarket
        });
        return processed;
    }
    processFitIndicatorsAction(action) {
        const processed = false;
        const window = action.Details;
        if (window) {
            const chart = this.chart;
            if (chart) {
                window.indicatorRendererSettings.UseInAutoscale = !window.indicatorRendererSettings.UseInAutoscale;
            }
        }
        return processed;
    }
    processFitOrdersAction(action) {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            chart.TerceraChartTradingToolsRenderer.OrderScale = !chart.TerceraChartTradingToolsRenderer.OrderScale;
        }
        return processed;
    }
    processFitAlertsAction(action) {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            chart.TerceraChartTradingToolsRenderer.AlertScale = !chart.TerceraChartTradingToolsRenderer.AlertScale;
        }
        return processed;
    }
    processFitDayHighLowAction(action) {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            chart.DayHighLowRenderer.UseInAutoscale = !chart.DayHighLowRenderer.UseInAutoscale;
        }
        return processed;
    }
    processFitDrawingsAction(action) {
        const processed = false;
        const window = action.Details;
        if (window) {
            const renderer = window.GetRenderer(TerceraChartToolRenderer);
            if (renderer) {
                renderer.UseInAutoscale = !renderer.UseInAutoscale;
            }
        }
        return processed;
    }
    processMainSymbolAction(action) {
        const processed = false;
        const overlay = action.Details;
        const chart = this.chart;
        if (overlay && chart) {
            chart.activeOverlay = overlay;
        }
        return processed;
    }
    processTimeSeparatorsAction(action) {
        const processed = false;
        const type = action.Details;
        if (type) {
            const renderer = this.chart.windowsContainer.xScaleRenderer;
            if (renderer) {
                const settings = renderer.settings;
                switch (type) {
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day:
                        settings.DaySeparatorsVisibility = !settings.DaySeparatorsVisibility;
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Week:
                        settings.WeekSeparatorsVisibility = !settings.WeekSeparatorsVisibility;
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month:
                        settings.MonthSeparatorsVisibility = !settings.MonthSeparatorsVisibility;
                        break;
                    case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year:
                        settings.YearSeparatorsVisibility = !settings.YearSeparatorsVisibility;
                        break;
                }
            }
        }
        return processed;
    }
    processShowHolesAction() {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            chart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.ShowEmptyBars), !chart.cashItemSeriesSettings.ShowEmptyBars);
        }
        return processed;
    }
    processShowExtendedSessionAction() {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            chart.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.ShowAllSession), !chart.ShowAllSessions);
        }
        return processed;
    }
    processOverlayAction(action) {
        const processed = false;
        const details = action.Details;
        const chart = this.chart;
        if (chart) {
            if (details === true) {
                chart.RemoveAllOverlays();
            }
            else if (typeof details === 'number') {
                chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.OverlayScreen, null);
            }
        }
        return processed;
    }
    processIndicatorAction(action) {
        const processed = false;
        const details = action.Details;
        const chart = this.chart;
        if (chart) {
            if (details === true) {
                chart.RemoveAllIndicators();
            }
            else if (chart && typeof details === 'number') {
                chart.terceraChartPanelContext.ShowScreen(details, null);
            }
        }
        return processed;
    }
    processRefreshAction() {
        let processed = false;
        const chart = this.chart;
        if (chart) {
            chart.RefreshChart(ChartLoadTrigger.RefreshAction);
            processed = true;
        }
        return processed;
    }
    processNewPanelAction(action) {
        const processed = false;
        const details = action.Details;
        if (details) {
            this.chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.NewPanel, {
                panelName: details
            });
        }
        return processed;
    }
    processDeleteToolsByTypeAction(action) {
        const processed = false;
        const chart = this.chart;
        const toolType = action.Details;
        if (!chart || typeof toolType !== 'number') {
            return;
        }
        const windows = chart.windowsContainer.Windows;
        const len = windows.length;
        for (let i = 0; i < len; i++) {
            const toolRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
            toolRenderer.RemoveToolsByType(toolType);
        }
        return processed;
    }
    processDeleteAllToolsAction() {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            const windows = chart.windowsContainer.Windows;
            const len = windows.length;
            for (let i = 0; i < len; i++) {
                const toolRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
                toolRenderer.RemoveAllTools();
            }
        }
        return processed;
    }
    processDeleteToolAction(action) {
        const processed = false;
        const details = action.Details;
        const chart = this.chart;
        if (chart) {
            const windows = chart.windowsContainer.Windows;
            for (const window of windows) {
                const toolRenderer = window.GetRenderer(TerceraChartToolRenderer);
                const toolView = this.getToolViewByTool(toolRenderer, details);
                if (toolView) {
                    toolRenderer.RemoveTool(toolView);
                    break;
                }
            }
        }
        return processed;
    }
    processToolSettingsAction(action) {
        const processed = false;
        const details = action.Details;
        const chart = this.chart;
        if (chart) {
            const windows = chart.windowsContainer.Windows;
            for (const window of windows) {
                const toolRenderer = window.GetRenderer(TerceraChartToolRenderer);
                const toolView = this.getToolViewByTool(toolRenderer, details);
                if (toolView) {
                    const parameters = { caller: toolView };
                    chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.Properties, parameters);
                    break;
                }
            }
        }
        return processed;
    }
    getToolViewByTool(toolRenderer, dataCacheTool) {
        for (const toolView of toolRenderer.tools) {
            if (toolView.dataCacheTool === dataCacheTool) {
                return toolView;
            }
        }
        return null;
    }
    processChartTradingAction(action) {
        const processed = false;
        const chart = this.chart;
        if (chart) {
            const details = action.Details;
            chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.ChartTrading, details === null || details === undefined ? null : { tradingFlag: details });
        }
        return processed;
    }
    processPlateIndicatorVisibleAction(action) {
        const processed = false;
        action.Details.Visible = !action.Details.Visible;
        return processed;
    }
    processPlateIndicatorDublicateAction(action) {
        const processed = false;
        const curIndicator = action.Details.PTLIndicator;
        const newIndicator = IndicatorManager.GetIndicator(action.Details.PTLIndicator.Script.ProjectName);
        newIndicator.callBack(curIndicator.Properties());
        //     newIndicator.OnNewHistory.Subscribe(function () { this.chart.IsDirty(); }, this);
        this.chart.AddIndicator(newIndicator, true);
        return processed;
    }
    processPlateIndicatorRemoveAction(action) {
        const processed = false;
        this.chart.RemoveIndicator(action.Details, true);
        return processed;
    }
    processPlateOverlayVisibleAction(action) {
        const processed = false;
        action.Details.mainPriceRenderer.Visible = !action.Details.mainPriceRenderer.Visible;
        return processed;
    }
    processPlateOverlayRemoveAction(action) {
        const processed = false;
        this.chart.RemoveOverlay(action.Details);
        return processed;
    }
    processCancelOrderSLTPAction(action) {
        const processed = false;
        const details = action.Details;
        this.chart.terceraChartPanelContext.ChartVisualTrading(details.order, {
            action: details.cancelSL ? TerceraChartTradingOperation.RemoveSL : TerceraChartTradingOperation.RemoveTP
        });
        return processed;
    }
    processDrawingToolsGroupAction(action) {
        action.Details.Visible = false;
        return false;
    }
    processToBeginAction(action) {
        this.chart.ToBegin();
        return true;
    }
    processObjectManagerAction(action) {
        this.OnActionProcessor.Raise(action);
        return true;
    }
    processShowOnChartAction(action) {
        const tool = action.Details;
        const points = tool.Points;
        const chart = this.chart;
        if (isNullOrUndefined(chart)) {
            return;
        }
        chart.goToAction(new Date(points[0][0]));
        chart.IsDirty();
        for (const tool of chart.AllWindowsHoverAndSelectedTools) {
            tool.ResetSelection();
        }
        const windows = chart.windowsContainer.Windows;
        for (const window of windows) {
            const toolRenderer = window.GetRenderer(TerceraChartToolRenderer);
            const toolView = this.getToolViewByTool(toolRenderer, tool);
            if (toolView) {
                toolView.SelectTool();
                break;
            }
        }
        return true;
    }
    processChangeDataTypeAction(action) {
        const processed = false;
        const window = action.Details;
        const cashItemSeriesSettings = (window === null || window === void 0 ? void 0 : window.TerceraChartOverlayWindow) && this.chart.activeOverlay
            ? this.chart.activeOverlay.cashItemSeriesSettings
            : this.chart.cashItemSeriesSettings;
        const prevDataType = cashItemSeriesSettings.DataType;
        let seriesDataType = TerceraChartCashItemSeriesDataType.Absolute;
        switch (action.Type) {
            case TerceraChartActionEnum.Absolute:
                seriesDataType = TerceraChartCashItemSeriesDataType.Absolute;
                break;
            case TerceraChartActionEnum.Relative:
                seriesDataType = TerceraChartCashItemSeriesDataType.Relative;
                break;
            case TerceraChartActionEnum.Log:
                seriesDataType = TerceraChartCashItemSeriesDataType.Log;
                break;
        }
        cashItemSeriesSettings.DataType = seriesDataType;
        // Manual scale is lost - we are trying to restore it
        if (!window.AutoScale && prevDataType !== seriesDataType) {
            let prevConverter = null;
            if (prevDataType === TerceraChartCashItemSeriesDataType.Relative) {
                prevConverter = cashItemSeriesSettings.relativeDataConverter;
            }
            else if (prevDataType === TerceraChartCashItemSeriesDataType.Log) {
                prevConverter = cashItemSeriesSettings.logDataConverter;
            }
            let newConverter = null;
            if (seriesDataType === TerceraChartCashItemSeriesDataType.Relative) {
                newConverter = cashItemSeriesSettings.relativeDataConverter;
            }
            else if (seriesDataType === TerceraChartCashItemSeriesDataType.Log) {
                newConverter = cashItemSeriesSettings.logDataConverter;
            }
            window.FminFloatY = ChartSeriesDataConverter.Convert(window.FminFloatY, prevConverter, newConverter);
            window.FmaxFloatY = ChartSeriesDataConverter.Convert(window.FmaxFloatY, prevConverter, newConverter);
        }
        return processed;
    }
    // #endregion
    // #region State processing.
    processSymbolsLookupState(outState) {
        outState.locKey = 'chart.instrumentsLookup';
    }
    processEraserState(outState) {
        outState.locKey = 'chart.tools.eraser';
        outState.svgImage = 'DeleteHoverAndSelectedTools';
        const chart = this.chart;
        if ((chart === null || chart === void 0 ? void 0 : chart.mainWindow) && chart.EraserRenderer) {
            outState.Active = chart.EraserRenderer.Active;
        }
        else {
            outState.Active = false;
        }
    }
    processToolSelectorState(outState) {
        outState.locKey = 'chart.tools.selection';
        outState.svgImage = 'ToolSelector';
        const chart = this.chart;
        if ((chart === null || chart === void 0 ? void 0 : chart.mainWindow) && chart.SelectionRenderer) {
            outState.Active = chart.SelectionRenderer.Active;
        }
        else {
            outState.Active = false;
        }
    }
    processCreationToolState(outState, action) {
        const toolType = action.Details;
        if (isNullOrUndefined(toolType)) {
            outState.locKey = 'chart.head.line';
            outState.svgImage = 'Drawings';
            return;
        }
        const tsi = TerceraChartActionProcessor.ToolStateInfoDict[toolType];
        outState.locKey = tsi.locKey;
        outState.svgImage = tsi.svgImage;
        const curTool = this.chart.NewToolStrategy.CurrentTool();
        outState.Active = !isNullOrUndefined(curTool) && curTool.ToolType === toolType;
    }
    plateIndicatorSettingsState(outState) {
        outState.locKey = 'chart.menu.PlateIndicatorSettings';
        outState.svgImage = 'menuSettings';
    }
    priceIndicatorState() { }
    setStayInDrawingModeState(outState) {
        outState.locKey = 'chart.tools.StayDrawingMode';
        if (this.chart) {
            const allRenderers = this.chart.mainWindow.GetAllRenderers();
            const len = allRenderers.length;
            for (let i = 0; i < len; i++) {
                const r = allRenderers[i];
                if (r instanceof TerceraChartNewToolRenderer) {
                    outState.Active = r ? r.StayInDrawingMode : false;
                    break;
                }
            }
        }
        else {
            outState.Active = false;
        }
    }
    deleteSelectedToolsState(outState) {
        outState.locKey = 'chart.tool.delete';
        outState.svgImage = 'Remove';
        const allWSelTools = this.chart.AllWindowsSelectedTools;
        outState.Visible = allWSelTools && allWSelTools.length > 0;
    }
    selectedToolSettingsState(outState) {
        outState.locKey = 'chart.tool.properties';
        outState.svgImage = 'menuSettings';
        const allWSelTools = this.chart.AllWindowsSelectedTools;
        outState.Visible = allWSelTools && allWSelTools.length === 1;
    }
    toolColorState(outState) {
        outState.Visible = false;
        const allSelectedTools = this.chart.AllWindowsHoverAndSelectedTools;
        if (allSelectedTools && allSelectedTools.length > 0) {
            const colorProp = DynProperty.getPropertyByName(allSelectedTools[0].Properties(), 'Color');
            if (colorProp === null || colorProp === void 0 ? void 0 : colorProp.value) {
                outState.Visible = true;
                outState.details = colorProp.value;
            }
        }
    }
    toolLineWidthState(outState) {
        outState.Visible = false;
        const allSelectedTools = this.chart.AllWindowsHoverAndSelectedTools;
        if (allSelectedTools && allSelectedTools.length > 0) {
            const widthProp = DynProperty.getPropertyByName(allSelectedTools[0].Properties(), 'Width');
            if (widthProp === null || widthProp === void 0 ? void 0 : widthProp.value) {
                outState.Visible = true;
                outState.details = widthProp.value;
            }
        }
    }
    processInfoWindowHideState(outState, action) {
        outState.locKey = 'chart.menu.InfoWindowHide';
    }
    processInfoWindowModeState(outState, action) {
        if (isNullOrUndefined(action.Details)) {
            outState.locKey = 'chart.view.dataInfo';
            outState.svgImage = 'Info window';
            return;
        }
        switch (action.Details) {
            case InfoWindowDisplayMode.SeparateWindow:
                outState.locKey = 'chart.menu.InfoWindowSeparateWindow';
                break;
            case InfoWindowDisplayMode.AttachedToCursor:
                outState.locKey = 'chart.menu.InfoWindowAttachedToCursor';
                break;
            case InfoWindowDisplayMode.Hidden:
                outState.locKey = 'chart.menu.InfoWindowHidden';
                break;
        }
        const infoWindowRenderer = this.chart.mainWindow.GetRenderer(TerceraChartInfoWindowRenderer);
        if (!isNullOrUndefined(infoWindowRenderer)) {
            outState.Active = infoWindowRenderer.displayMode === action.Details;
        }
    }
    processInfoWindowDockState(outState, action) {
        outState.locKey = 'chart.menu.InfoWindowDock';
        outState.Active = action.Details.DockingType !== InfoWindowDockingType.None;
    }
    processInfoWindowShortModeState(outState, action) {
        outState.locKey = 'chart.menu.InfoWindowShortMode';
        outState.Active = action.Details.ShortMode;
    }
    processInfoWindowSettingsState(outState) {
        outState.locKey = 'chart.menu.InfoWindowSettings';
        outState.svgImage = 'menuSettings';
    }
    plateOverlaySettingsState(outState) {
        outState.locKey = 'chart.menu.PlateOverlaySettings';
        outState.svgImage = 'menuSettings';
    }
    processHoverAndSelectedToolSettingsState(outState) {
        outState.locKey = 'chart.tool.properties';
        outState.svgImage = 'menuSettings';
        outState.Visible = this.chart.AllWindowsHoverAndSelectedTools.length > 0;
    }
    processCloneToolState(outState) {
        outState.locKey = 'chart.tool.clone';
        outState.svgImage = 'Clone';
    }
    processDeleteHoverAndSelectedToolsState(outState) {
        outState.locKey = 'chart.tool.delete';
        outState.svgImage = 'Remove';
        outState.Visible = this.chart.AllWindowsHoverAndSelectedTools.length > 0;
    }
    processStyleState(outState, action) {
        const chart = this.chart;
        if (!chart)
            return;
        const tfInfo = chart.TimeFrameInfo();
        if (!tfInfo)
            return;
        const drawingStyle = action.Details;
        if (drawingStyle !== undefined && drawingStyle !== null) {
            outState.locKey = TerceraChartUtils.ToLocalizedTerceraChartDrawingType(drawingStyle);
            outState.svgImage = TerceraChartUtils.getChartDrawingIconByType(drawingStyle);
            outState.Visible = chart.model.GetAllowedStyles(tfInfo).includes(drawingStyle);
            outState.Active = chart.mainPriceRenderer.ChartDrawingType === drawingStyle;
        }
        else {
            outState.locKey = 'chart.head.charttype';
            outState.svgImage = TerceraChartUtils.getChartDrawingIconByType(chart.mainPriceRenderer.ChartDrawingType);
            outState.Enabled = chart.model.GetAllowedStyles(tfInfo).length > 1;
        }
    }
    processDataTypeState(outState, action) {
        const historyType = action.Details;
        const chart = this.chart;
        const instrument = chart.Instrument();
        const tfInfo = chart.TimeFrameInfo();
        if (historyType && instrument && tfInfo) {
            const historyState = TerceraChartActionState.GetHistoryTypeActionState(action.Details, tfInfo.Periods, instrument, chart.ChartHistoryType());
            outState.applyFrom(historyState);
        }
        else {
            outState.locKey = 'chart.view.source';
        }
        outState.Enabled = tfInfo ? tfInfo.TradeAnalysisType() !== TradeAnalysisType.Cluster : true;
    }
    processClosePositionState(outState, action) {
        outState.locKey = 'chart.menu.close';
        outState.Enabled = IsAllowed.IsPositionClosingAllowed(action.Details, true).Allowed;
        outState.Visible = true;
    }
    processCloseAllPositionState(outState) {
        outState.locKey = 'chart.menu.closeall';
        outState.Enabled =
            !TradingLockUtils.TradingLock.tradingLocked &&
                !DataCache.isAllowedForAccount(RulesSet.TERMINAL_HTML) &&
                IsAllowed.IsCloseAllAllowed(true, false).Allowed;
        outState.Visible = !DataCache.isAllowedForAccount(RulesSet.FUNCTION_FIFO_POSITIONS);
    }
    processModifyPositionState(outState, action) {
        const position = action.Details;
        outState.locKey = 'chart.menu.modify';
        outState.Enabled = position && IsAllowed.IsPositionModifyingAllowed(position).Allowed;
        outState.Visible = true;
    }
    processRollbackPositionState(outState) {
        outState.locKey = 'chart.menu.rollback';
        outState.Enabled = true;
        outState.Visible = IsAllowed.IsRollbackAllowed().Allowed;
    }
    processCancelOrdersState(outState, action) {
        outState.locKey = 'chart.menu.cancelOrder';
        outState.Enabled = IsAllowed.IsOrderCancelingAllowed(action.Details).Allowed;
        outState.Visible = true;
    }
    processCancelOrderIDState(outState, action) {
        outState.locKey =
            Resources.getResource('chart.menu.cancelOrder') +
                ' ' +
                (action.Details instanceof Position ? action.Details.PositionId : action.Details.OrderNumber);
        outState.Enabled = IsAllowed.IsOrderCancelingAllowed(action.Details).Allowed;
        outState.Visible = true;
    }
    processCancelOrdersAccountState(outState) {
        outState.locKey = 'chart.menu.cancelByAccount';
        outState.Enabled = IsAllowed.IsCloseAllAllowed(false).Allowed;
        outState.Visible = true;
    }
    processCancelOrdersSymbolState(outState) {
        outState.locKey = 'chart.menu.cancelByInstrument';
        outState.Enabled = IsAllowed.IsCloseAllAllowed(false).Allowed;
        outState.Visible = true;
    }
    processModifyOrderState(outState, action) {
        outState.locKey = 'chart.menu.modify';
        outState.Visible = true;
        const details = action.Details;
        outState.Enabled = details.isPosition
            ? IsAllowed.IsPositionModifyingAllowed(details).Allowed
            : IsAllowed.IsOrderModifyingAllowed(details).Allowed;
    }
    processChangeToMarketState(outState, action) {
        outState.locKey = 'panel.orders.menu.ExecuteAsMarket';
        outState.Enabled = IsAllowed.IsOrderExecutingAllowed(action.Details).Allowed;
        outState.Visible = true;
    }
    processFitIndicatorsState(outState, action) {
        const window = action.Details;
        if (window) {
            outState.Active = window.indicatorRendererSettings.UseInAutoscale;
        }
        outState.locKey = 'chart.priceScaleContextMenu.FitIndicators';
    }
    processFitOrdersState(outState) {
        const chart = this.chart;
        if (chart) {
            outState.Active = chart.TerceraChartTradingToolsRenderer.OrderScale;
        }
        outState.locKey = 'chart.priceScaleContextMenu.FitOrdersPositions';
    }
    processFitAlertsState(outState) {
        const chart = this.chart;
        if (chart) {
            outState.Active = chart.TerceraChartTradingToolsRenderer.AlertScale;
        }
        outState.Visible =
            !Resources.isHidden('screen.Alerts.visibility') && DataCache.isAllowedForMyUser(RulesSet.FUNCTION_ALERTS);
        outState.locKey = 'chart.priceScaleContextMenu.FitAlerts';
    }
    processFitDayHighLowState(outState) {
        if (!this.chart.DayHighLowRenderer) {
            outState.Visible = false;
            return;
        }
        if (this.chart) {
            outState.Active = this.chart.DayHighLowRenderer.UseInAutoscale;
        }
        outState.locKey = 'chart.priceScaleContextMenu.FitDay';
    }
    processFitDrawingsState(outState, action) {
        const window = action.Details;
        if (window) {
            const renderer = window.GetRenderer(TerceraChartToolRenderer);
            if (renderer) {
                outState.Active = renderer.UseInAutoscale;
            }
            else {
                outState.Visible = false;
            }
        }
        outState.locKey = 'chart.priceScaleContextMenu.FitDrawings';
    }
    processMainSymbolState(outState, action) {
        const overlay = action.Details;
        const chart = this.chart;
        if (overlay && chart) {
            outState.Active = chart.activeOverlay === overlay;
            outState.locKey = overlay.ActiveInstrument.toString();
        }
    }
    processTimeSeparatorsState(outState, action) {
        const type = action.Details;
        if (!type) {
            outState.locKey = 'chart.timeScaleContextMenu.ShowTimePeriodsSeparator';
            return;
        }
        const renderer = this.chart.windowsContainer.xScaleRenderer;
        if (!renderer)
            return;
        const settings = renderer.settings;
        switch (type) {
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day:
                outState.locKey = 'chart.timeScaleContextMenu.ShowDaySeparator';
                outState.Active = settings.DaySeparatorsVisibility;
                break;
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Week:
                outState.locKey = 'chart.timeScaleContextMenu.ShowWeeksSeparator';
                outState.Active = settings.WeekSeparatorsVisibility;
                break;
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month:
                outState.locKey = 'chart.timeScaleContextMenu.ShowMonthSeparator';
                outState.Active = settings.MonthSeparatorsVisibility;
                break;
            case TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year:
                outState.locKey = 'chart.timeScaleContextMenu.ShowYearSeparator';
                outState.Active = settings.YearSeparatorsVisibility;
                break;
        }
    }
    processShowHolesState(outState) {
        const chart = this.chart;
        if (chart) {
            outState.Enabled = chart.TimeFrameInfo().ChartDataType !== ChartDataType.HeikinAshi;
            outState.Active = chart.cashItemSeriesSettings.ShowEmptyBars;
        }
        outState.locKey = 'chart.timeScaleContextMenu.ShowEmptyBars';
    }
    processShowExtendedSessionState(outState) {
        const chart = this.chart;
        if (chart) {
            outState.Active = chart.ShowAllSessions;
        }
        outState.locKey = 'chart.timeScaleContextMenu.ShowAllSessions';
    }
    processOverlayState(outState, action) {
        const details = action.Details;
        if (details === true) {
            const chart = this.chart;
            if (chart) {
                outState.Enabled = chart.Overlays.length > 0;
            }
            outState.svgImage = 'Remove';
            outState.locKey = 'chart.head.overlayRemoveAll';
        }
        else if (typeof details === 'number') {
            outState.svgImage = 'Overlays';
            outState.locKey = 'chart.head.addOverlay';
        }
        else {
            outState.svgImage = 'Overlays';
            outState.locKey = 'chart.head.overlays';
        }
    }
    processIndicatorState(outState, action) {
        const details = action.Details;
        if (details === true) {
            const chart = this.chart;
            if (chart)
                outState.Enabled = chart.IndicatorRenderers().length > 0;
            outState.locKey = 'chart.indicatorRemoveAll';
            outState.svgImage = 'Remove';
        }
        else if (typeof details === 'number') {
            outState.locKey = 'chart.indicatorAdd';
            outState.svgImage = 'AddIndicator';
        }
        else {
            outState.locKey = 'chart.indicators';
            outState.svgImage = 'AddIndicator';
        }
    }
    processViewState(outState, action) {
        const details = action.Details;
        const chart = this.chart;
        if (details === undefined || details === null || !chart) {
            outState.locKey = 'chart.view';
            outState.svgImage = 'View';
            return;
        }
        switch (details) {
            // TODO.
            case TerceraChartToolbarsEnum.AnalyseToolBar:
                outState.Visible = false;
                break;
            case TerceraChartToolbarsEnum.DrawingToolBar:
                outState.locKey = 'chart.view.drawingtoolbar';
                outState.Active = chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.UIManipulation, {
                    isSet: false,
                    toolBarType: TerceraChartToolbarsEnum.DrawingToolBar
                });
                break;
            case TerceraChartToolbarsEnum.MainToolBar:
                outState.locKey = 'chart.view.toolbar';
                outState.Active = chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.UIManipulation, {
                    isSet: false,
                    toolBarType: TerceraChartToolbarsEnum.MainToolBar
                });
                break;
            case TerceraChartToolbarsEnum.OrderEntry:
                outState.locKey = 'chart.view.orderEntry';
                outState.Active = chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.UIManipulation, {
                    isSet: false,
                    toolBarType: TerceraChartToolbarsEnum.OrderEntry
                });
                break;
            case TerceraChartToolbarsEnum.PreviousCloseLine:
                outState.Visible = false;
                if (typeof TerceraChartDayHighLowRenderer === 'function') {
                    const renderer = chart.mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
                    if (renderer) {
                        outState.Visible = true;
                        outState.locKey = 'chart.view.previousCloseLine';
                        outState.Enabled = renderer.Enabled();
                        outState.Active = renderer.PrevCloseVisibleGet();
                    }
                }
                break;
            case TerceraChartToolbarsEnum.TimeToNextBar:
                outState.locKey = 'chart.view.TimeToNextBar';
                outState.Active = chart.mainWindow.GetRenderer(TerceraChartTimeToNextBarRenderer).Visible;
                break;
            case TerceraChartToolbarsEnum.DayHighLow:
                outState.Visible = false;
                if (typeof TerceraChartDayHighLowRenderer === 'function') {
                    const renderer = chart.mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
                    if (renderer) {
                        outState.Visible = true;
                        outState.locKey = 'chart.view.Day_HighLow';
                        outState.Enabled = renderer.Enabled();
                        outState.Active = renderer.VisibleGet();
                    }
                }
                break;
            case TerceraChartToolbarsEnum.Volume:
                outState.locKey = 'chart.mainlayer.volume';
                outState.Active = chart.volumeBarsRenderer.Visible;
                outState.Visible =
                    !Resources.isHidden('property.hotkeyManager.ShowVolume') ||
                        DataCache.isAllowedForMyUser(RulesSet.FUNCTION_CHART_VOLUME_BARS);
                break;
            case TerceraChartToolbarsEnum.GridTime:
                outState.locKey = 'chart.mainlayer.gridTime';
                outState.Active = chart.xScaleRendererSettings.ScaleGridVisibility;
                break;
            case TerceraChartToolbarsEnum.GridPrice:
                outState.locKey = 'chart.mainlayer.gridPrice';
                outState.Active = chart.yScaleRendererSettings.ScaleGridVisibility;
                break;
        }
    }
    processRefreshState(outState) {
        outState.Enabled = true;
        outState.svgImage = 'refreshButton';
        outState.locKey = 'chart.refresh';
    }
    processNewPanelState(outState, action) {
        switch (action.Details) {
            /* case PanelNames.ChartPanel:
                        outState.locKey = "panel.copypanel";
                        // TODO.
                        //outState.Visible = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING);
                        outState.Visible = true;
                        break; */
            case PanelNames.AdvancedOrderEntry:
                outState.locKey = 'panel.neworder';
                outState.svgImage = 'orderEntry';
                outState.Visible = CustomerAccess.panelAllowed(PanelNames.AdvancedOrderEntry);
                break;
            case PanelNames.MarketDepthPanel:
                outState.locKey = 'panel.level2';
                outState.svgImage = 'MD';
                outState.Visible = CustomerAccess.panelAllowed(PanelNames.MarketDepthPanel);
                break;
            /* TODO.
        case Utils.NAME_NEWTRADES_PANEL:
            outState.locKey = "panel.trades";
            outState.Visible = DataCache.isAllowed(RulesSet.FUNCTION_TRADES, null);
            break; */
            default:
                outState.locKey = 'panel.menu.new';
                outState.svgImage = 'New';
                break;
        }
    }
    processDeleteToolsByTypeState(outState, action) {
        const chart = this.chart;
        const toolType = action.Details;
        if (isNullOrUndefined(toolType)) {
            outState.locKey = 'chart.tool.delete';
            outState.svgImage = 'Remove';
            return;
        }
        if (isNullOrUndefined(chart))
            return;
        const toolState = TerceraChartActionProcessor.ToolStateInfoDict[toolType];
        outState.locKey = toolState.locKey + '.deleteByType';
        outState.svgImage = toolState.svgImage;
        outState.Visible = false;
        const windows = chart.windowsContainer.Windows;
        const len = windows.length;
        for (let i = 0; i < len; i++) {
            const toolRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
            if (isNullOrUndefined(toolRenderer)) {
                continue;
            }
            const tools = toolRenderer.tools;
            if (isNullOrUndefined(tools)) {
                continue;
            }
            const t_len = tools.length;
            for (let t_i = 0; t_i < t_len; t_i++) {
                const tool = tools[t_i];
                if (tool.dataCacheTool.ToolType === toolType) {
                    outState.Visible = true;
                    return;
                }
            }
        }
    }
    processDeleteAllToolsState(outState) {
        outState.locKey = 'chart.tool.deleteAll';
        outState.svgImage = 'Remove';
        outState.Enabled = false;
        const chart = this.chart;
        if (!isNullOrUndefined(chart)) {
            const windows = chart.windowsContainer.Windows;
            const len = windows.length;
            for (let i = 0; i < len; i++) {
                const toolRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
                if (toolRenderer.tools.length > 0) {
                    outState.Enabled = true;
                    break;
                }
            }
        }
    }
    processChartTradingState(outState) {
        outState.Active = this.chart.TerceraChartTradingToolsRenderer.QuickOEMode();
    }
    processPlateIndicatorVisibleState(outState, action) {
        outState.locKey = 'chart.menu.PlateIndicatorVisible';
        outState.Active = action.Details.Visible;
        outState.svgImage = outState.Active ? 'Visible' : 'Invisible';
    }
    processPlateIndicatorDublicateState(outState, action) {
        outState.Visible = action.Details.AllowDublicate();
        outState.locKey = 'chart.menu.PlateIndicatorDublicate';
        outState.svgImage = 'Clone';
    }
    processPlateIndicatorRemoveState(outState, action) {
        outState.locKey = 'chart.menu.PlateIndicatorRemove';
        outState.svgImage = 'Remove';
    }
    processPlateOverlayVisibleState(outState, action) {
        outState.locKey = 'chart.menu.PlateOverlayVisible';
        outState.Active = action.Details.mainPriceRenderer.Visible;
        outState.svgImage = outState.Active ? 'Visible' : 'Invisible';
    }
    processPlateOverlayRemoveState(outState, action) {
        outState.locKey = 'chart.menu.PlateOverlayRemove';
        outState.svgImage = 'Remove';
    }
    processCancelOrderSLTPState(outState, action) {
        const details = action.Details;
        outState.locKey = details.cancelSL ? 'TradeTool.Cancel SL' : 'TradeTool.Cancel TP';
    }
    processToBeginState(outState) {
        const chart = this.chart;
        if (isNullOrUndefined(chart)) {
            return;
        }
        const cashItem = this.chart.MainCashItemSeries.CashItem;
        outState.ToolTiplocKey = 'chart.chartScroll.toBegin';
        outState.Enabled = !chart.IsAtTheBegin(chart.mainWindow.i1) || !cashItem.isRealTimeBuilding;
    }
    processObjectManagerState(outState) {
        outState.locKey = 'ChartObjectManager.Text';
        outState.svgImage = 'Object manager';
        outState.Enabled = true;
    }
    processChangeDataTypeState(outState, action) {
        const window = action.Details;
        const cashItemSeriesSettings = (window === null || window === void 0 ? void 0 : window.TerceraChartOverlayWindow) && this.chart.activeOverlay
            ? this.chart.activeOverlay.cashItemSeriesSettings
            : this.chart.cashItemSeriesSettings;
        switch (action.Type) {
            case TerceraChartActionEnum.Absolute:
                outState.Active = cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Absolute;
                outState.svgImage = 'Absolute';
                outState.locKey = 'property.GraphicType.Absolute';
                outState.ToolTiplocKey = 'property.GraphicType.Absolute';
                break;
            case TerceraChartActionEnum.Relative:
                outState.Active = cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Relative;
                outState.svgImage = 'Relative';
                outState.locKey = 'property.GraphicType.Relative';
                outState.ToolTiplocKey = 'property.GraphicType.Relative';
                break;
            case TerceraChartActionEnum.Log:
                outState.Active = cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Log;
                outState.svgImage = 'Logarithmic';
                outState.locKey = 'property.GraphicType.Log';
                outState.ToolTiplocKey = 'property.GraphicType.Log';
                break;
        }
    }
    // #endregion
    processCancelAction(action) {
        let processed = super.processCancelAction(action);
        let context = CancelActionContext.Esc;
        const details = action.Details;
        // Old condition: action.Details is CancelActionContext
        if (typeof details === 'number') {
            context = details;
        }
        processed =
            this.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Eraser, false), true) ||
                processed;
        processed =
            this.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ToolSelector, false), true) || processed;
        if (context === CancelActionContext.Esc) {
            processed =
                this.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ChartTrading, false), true) || processed;
        }
        processed = this.chart.NewToolStrategy.Cancel() || processed;
        return processed;
    }
    processZoomState(outState, action) {
        super.processZoomState(outState, action);
        this.chart.resetCurrentView();
    }
    processCancelState(outState) {
        outState.locKey = 'chart.tools.default';
        const chart = this.chart;
        outState.Active =
            !chart.NewToolStrategy.CurrentTool() &&
                !this.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ScalingByRect)).Active &&
                !this.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Eraser)).Active &&
                !this.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ToolSelector)).Active &&
                !this.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ChartTrading)).Active;
    }
    // #endregion
    shouldCancelAction(actionType) {
        return actionType !== TerceraChartActionEnum.InfoWindowMode && super.shouldCancelAction(actionType);
    }
}
// #region Tools State Info Dictionary
TerceraChartActionProcessor.ToolStateInfoDict = {
    [DataCacheToolType.Line]: {
        locKey: 'chart.tools.Line',
        svgImage: 'Line'
    },
    [DataCacheToolType.HorizontalLine]: {
        locKey: 'chart.tools.horizontal',
        svgImage: 'HorizontalLine'
    },
    [DataCacheToolType.VerticalLine]: {
        locKey: 'chart.tools.vertical',
        svgImage: 'VerticalLine'
    },
    [DataCacheToolType.Triangle]: {
        locKey: 'chart.tools.Triangle',
        svgImage: 'Triangle'
    },
    [DataCacheToolType.Rectangle]: {
        locKey: 'chart.tools.Rectangle',
        svgImage: 'Rectangle'
    },
    [DataCacheToolType.Polygon]: {
        locKey: 'chart.tools.Polygon',
        svgImage: 'Polygon'
    },
    [DataCacheToolType.Circle]: {
        locKey: 'chart.tools.Circle',
        svgImage: 'Circle'
    },
    [DataCacheToolType.Ellipse]: {
        locKey: 'chart.tools.Ellipse',
        svgImage: 'Ellipse'
    },
    [DataCacheToolType.AndrewsPitchFork]: {
        locKey: 'chart.tools.AndrewPitchfork',
        svgImage: 'AndrewsPitchFork'
    },
    [DataCacheToolType.PriceChannel]: {
        locKey: 'chart.tools.PriceChannel',
        svgImage: 'PriceChannel'
    },
    [DataCacheToolType.FibonacciRetracement]: {
        locKey: 'chart.tools.Fibretr',
        svgImage: 'FibonacciRetracement'
    },
    [DataCacheToolType.FibonacciExpansion]: {
        locKey: 'chart.tools.FibExp',
        svgImage: 'FibonacciExpansion'
    },
    [DataCacheToolType.FibonacciArc]: {
        locKey: 'chart.tools.FibArc',
        svgImage: 'FibonacciArc'
    },
    [DataCacheToolType.FibonacciSpiral]: {
        locKey: 'chart.tools.FibSpiral',
        svgImage: 'FibonacciSpiral'
    },
    [DataCacheToolType.FibonacciEllipse]: {
        locKey: 'chart.tools.FibEllipses',
        svgImage: 'FibonacciEllipse'
    },
    [DataCacheToolType.FibonacciFans]: {
        locKey: 'chart.tools.FibFan',
        svgImage: 'FibonacciFans'
    },
    [DataCacheToolType.FibonacciPhiChannel]: {
        locKey: 'chart.tools.FibPhiChannel',
        svgImage: 'FibonacciPhiChannel'
    },
    [DataCacheToolType.FibonacciTimeGoal]: {
        locKey: 'chart.tools.FibTga',
        svgImage: 'FibonacciTimeGoal'
    },
    [DataCacheToolType.FibonacciTimeZone]: {
        locKey: 'chart.tools.FibTz',
        svgImage: 'FibonacciTimeZone'
    },
    [DataCacheToolType.GannLine]: {
        locKey: 'chart.tools.GannLine',
        svgImage: 'GannLine'
    },
    [DataCacheToolType.GannFan]: {
        locKey: 'chart.tools.GannFan',
        svgImage: 'GannFan'
    },
    [DataCacheToolType.GannGrid]: {
        locKey: 'chart.tools.GannGrid',
        svgImage: 'GannGrid'
    },
    [DataCacheToolType.ElliottImpulse]: {
        locKey: 'chart.tools.ElliottImpulse',
        svgImage: 'ElliottImpulse'
    },
    [DataCacheToolType.ElliottCorrection]: {
        locKey: 'chart.tools.ElliottCorrection',
        svgImage: 'ElliottCorrection'
    },
    [DataCacheToolType.ElliottTriangle]: {
        locKey: 'chart.tools.ElliottTriangle',
        svgImage: 'ElliottTriangle'
    },
    [DataCacheToolType.ElliottTripleCombo]: {
        locKey: 'chart.tools.ElliottTripleCombo',
        svgImage: 'ElliottTripleCombo'
    },
    [DataCacheToolType.ElliottDoubleCombo]: {
        locKey: 'chart.tools.ElliottDoubleCombo',
        svgImage: 'ElliottDoubleCombo'
    },
    [DataCacheToolType.InfoLine]: {
        locKey: 'chart.tools.infoline',
        svgImage: 'InfoLine'
    },
    [DataCacheToolType.ArrowLabel]: {
        locKey: 'chart.tools.arrowLabel',
        svgImage: 'ArrowLabel'
    },
    [DataCacheToolType.CommentsLabel]: {
        locKey: 'chart.tools.commentsLabel',
        svgImage: 'CommentsLabel'
    },
    [DataCacheToolType.PriceLabel]: {
        locKey: 'chart.tools.priceLabel',
        svgImage: 'PriceLabel'
    },
    [DataCacheToolType.Symbol]: {
        locKey: 'chart.tools.symbol'
    },
    [DataCacheToolType.Text]: {
        locKey: 'chart.tools.text',
        svgImage: 'CommentsLabel'
    },
    [DataCacheToolType.RegressionChannel]: {
        locKey: 'chart.tools.RegressionChannel',
        svgImage: 'RegressionChannel'
    },
    [DataCacheToolType.ABCPatern]: {
        locKey: 'chart.tools.ABCPatern'
    },
    [DataCacheToolType.PPZ]: {
        locKey: 'chart.tools.PPZ'
    },
    [DataCacheToolType.TopBottomPattern]: {
        locKey: 'chart.tools.TopBottomPattern'
    },
    [DataCacheToolType.ButterflyGartley]: {
        locKey: 'chart.tools.ButterflyGartley'
    },
    [DataCacheToolType.FibonacciTools]: {
        locKey: 'chart.tools.FibonacciTools'
    },
    [DataCacheToolType.GannTools]: {
        locKey: 'chart.tools.GannTools'
    },
    [DataCacheToolType.ElliottTools]: {
        locKey: 'chart.tools.ElliottTools'
    },
    [DataCacheToolType.GeometryTools]: {
        locKey: 'chart.tools.GeometryTools'
    },
    [DataCacheToolType.TrendTools]: {
        locKey: 'chart.tools.TrendTools'
    },
    [DataCacheToolType.SecondaryTools]: {
        locKey: 'chart.tools.SecondaryTools'
    }
};
// #endregion
